import {
  ArrowRight,
  Footprints,
  AlertTriangle,
  Check,
  Copy,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { walkthroughStatusLabel } from "@/components/experience/vocabulary";
import { cn } from "@/lib/utils";
import type { ComparisonMode, ComparisonRunInfo } from "./comparison-types";

export interface ComparisonControlBarProps {
  candidateRuns: ComparisonRunInfo[];
  activeBaseRunId: string | null;
  onSelectBaseRun: (val: string) => void;
  runNumbers: Map<string, number>;
  currentRunNumber?: number;
  mode: ComparisonMode;
  setMode: (mode: ComparisonMode) => void;
  journeyStepCount?: number;
  totalDiffs: number;
  markdownSummary?: string | null;
  onCopyMarkdown: () => void;
  copiedMd: boolean;
}

export function ComparisonControlBar({
  candidateRuns,
  activeBaseRunId,
  onSelectBaseRun,
  runNumbers,
  currentRunNumber,
  mode,
  setMode,
  journeyStepCount,
  totalDiffs,
  markdownSummary,
  onCopyMarkdown,
  copiedMd,
}: ComparisonControlBarProps) {
  return (
    <div className="flex flex-wrap items-center justify-between gap-3 border-b border-border/60 pb-3">
      {/* Left: Selector Pair */}
      <div className="flex flex-wrap items-center gap-2 min-w-0">
        <span className="text-xs font-medium text-muted-foreground shrink-0">
          Baseline:
        </span>

        {/* Base Run Selector */}
        <Select
          value={activeBaseRunId ?? ""}
          onValueChange={(val) => onSelectBaseRun(val)}
        >
          <SelectTrigger className="h-8 text-xs font-medium min-w-[180px] sm:min-w-[200px] max-w-[280px] truncate bg-background">
            <SelectValue placeholder="Select baseline run" />
          </SelectTrigger>
          <SelectContent>
            {candidateRuns.map((r) => {
              const num = runNumbers.get(r.id);
              const pCount = r.problemCounts?.found ?? r.findingCount ?? 0;
              return (
                <SelectItem key={r.id} value={r.id} className="text-xs">
                  Run #{num} · {walkthroughStatusLabel(r.status)} · {pCount}{" "}
                  {pCount === 1 ? "problem" : "problems"}
                </SelectItem>
              );
            })}
          </SelectContent>
        </Select>

        <ArrowRight className="size-3.5 text-muted-foreground shrink-0" />

        {/* Current Run Chip */}
        <div className="inline-flex items-center h-8 px-2.5 rounded-md border border-border/80 bg-muted/40 text-xs font-medium shrink-0 gap-1.5">
          <span className="font-semibold text-foreground">
            Run #{currentRunNumber ?? "?"}
          </span>
          <span className="text-muted-foreground">(current)</span>
        </div>
      </div>

      {/* Right: View Mode Segmented Control + Copy Markdown */}
      <div className="flex flex-wrap items-center gap-2 shrink-0">
        {/* View Mode Switcher */}
        <div className="inline-flex items-center rounded-md bg-muted/60 p-0.5 border border-border/70 text-xs">
          <button
            type="button"
            onClick={() => setMode("journey")}
            className={cn(
              "flex items-center gap-1.5 px-3 py-1 rounded-sm text-xs font-medium transition-colors cursor-pointer",
              mode === "journey"
                ? "bg-foreground text-background font-semibold shadow-xs"
                : "text-muted-foreground hover:text-foreground",
            )}
          >
            <Footprints className="size-3.5" />
            <span>Journey trace</span>
            {typeof journeyStepCount === "number" && (
              <span className="ml-0.5 font-mono text-xs tabular-nums">
                ({journeyStepCount})
              </span>
            )}
          </button>

          <button
            type="button"
            onClick={() => setMode("findings")}
            className={cn(
              "flex items-center gap-1.5 px-3 py-1 rounded-sm text-xs font-medium transition-colors cursor-pointer",
              mode === "findings"
                ? "bg-foreground text-background font-semibold shadow-xs"
                : "text-muted-foreground hover:text-foreground",
            )}
          >
            <AlertTriangle className="size-3.5" />
            <span>Findings delta</span>
            <span className="ml-0.5 font-mono text-xs tabular-nums">
              ({totalDiffs})
            </span>
          </button>
        </div>

        {/* Copy Markdown Button */}
        {markdownSummary && (
          <Button
            variant="outline"
            size="sm"
            className="text-xs gap-1.5 shrink-0"
            onClick={onCopyMarkdown}
            title="Copy markdown summary of this run comparison"
          >
            {copiedMd ? (
              <>
                <Check className="size-3.5 text-emerald-600 dark:text-emerald-400" />
                <span>Copied</span>
              </>
            ) : (
              <>
                <Copy className="size-3.5 text-muted-foreground" />
                <span>Copy diff</span>
              </>
            )}
          </Button>
        )}
      </div>
    </div>
  );
}
